import React, { useMemo, useState } from "react";
import { FileDown, Printer } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import VoucherDocumentActions from "./VoucherDocumentActions";
import { extractError } from "../lib/api";
import { downloadApiFile, openApiPrintView } from "../lib/downloadApiFile";
import { cn } from "../lib/utils";
import { purchaseRegisterPrintItems } from "../lib/accountsSettlement";

/**
 * Print / PDF for every voucher on one purchase, plus Print all / PDF all pack.
 */
export default function PurchaseVoucherPrintBar({
  group,
  purchaseId: purchaseIdProp,
  compact = false,
  className = "",
}) {
  const purchaseId = purchaseIdProp || group?.purchase_id;
  const items = useMemo(() => purchaseRegisterPrintItems(group), [group]);
  const [busy, setBusy] = useState("");

  if (!items.length && !purchaseId) return null;

  const packCode = group?.purchase_code || items[0]?.code || "vouchers";
  const packPrint = async () => {
    if (!purchaseId) return;
    setBusy("print-all");
    try {
      await openApiPrintView(`/purchases/${purchaseId}/voucher-pack/print`);
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setBusy("");
    }
  };
  const packPdf = async () => {
    if (!purchaseId) return;
    setBusy("pdf-all");
    try {
      await downloadApiFile(
        `/purchases/${purchaseId}/voucher-pack/pdf`,
        `${String(packCode).replace(/[/\\?%*:|"<>]/g, "-")}-vouchers.pdf`,
      );
      toast.success("Purchase voucher pack downloaded");
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setBusy("");
    }
  };

  if (compact) {
    return (
      <div
        className={cn(
          "px-3 py-1.5 border-t border-border/60 bg-muted/15",
          "flex flex-wrap items-center gap-1",
          className,
        )}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
        role="presentation"
      >
        <span className="text-[9px] uppercase tracking-[0.14em] text-muted-foreground font-medium shrink-0 mr-0.5">
          Print
        </span>
        {purchaseId ? (
          <div className="inline-flex items-center gap-0.5 rounded-md border border-bansal-sage/30 bg-card pl-1.5 pr-0.5">
            <span className="text-[9px] uppercase tracking-wide text-primary font-semibold px-0.5">All</span>
            <Button
              type="button"
              size="icon"
              variant="ghost"
              className="h-7 w-7 text-muted-foreground hover:text-foreground"
              onClick={packPrint}
              disabled={busy === "print-all"}
              title={`Print all vouchers (${packCode})`}
              aria-label={`Print all vouchers ${packCode}`}
              data-testid={`print-pack-${packCode}`}
            >
              <Printer className="h-3.5 w-3.5" />
            </Button>
            <Button
              type="button"
              size="icon"
              variant="ghost"
              className="h-7 w-7 text-muted-foreground hover:text-foreground"
              onClick={packPdf}
              disabled={busy === "pdf-all"}
              title={`Download all vouchers PDF (${packCode})`}
              aria-label={`Download all vouchers PDF ${packCode}`}
              data-testid={`pdf-pack-${packCode}`}
            >
              <FileDown className="h-3.5 w-3.5" />
            </Button>
          </div>
        ) : null}
        {items.map((item) => (
          <div
            key={item.id}
            className="inline-flex items-center gap-0.5 rounded-md border border-border/70 bg-card pl-1.5 pr-0.5"
            title={`${item.label}: ${item.code}`}
          >
            <span className="text-[9px] uppercase tracking-wide text-muted-foreground font-medium px-0.5">
              {item.short}
            </span>
            <VoucherDocumentActions
              label={item.label}
              voucherId={item.id}
              voucherCode={item.code}
              compact
            />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div
      className={cn(
        "rounded-xl lg:rounded-lg border border-border bg-muted/30 px-3 py-3 space-y-2.5",
        className,
      )}
      data-testid={`purchase-print-bar-${packCode}`}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-xs font-semibold text-foreground">Voucher printing</p>
          <p className="text-[11px] text-muted-foreground">
            Print each voucher, or all related vouchers for this purchase.
          </p>
        </div>
        {purchaseId ? (
          <div className="flex items-center gap-1.5">
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-8 text-xs border-bansal-sage/40 text-primary hover:bg-bansal-sageLight/40"
              onClick={packPrint}
              disabled={busy === "print-all"}
              data-testid={`print-pack-${packCode}`}
            >
              <Printer className="h-3.5 w-3.5 mr-1" />
              Print all
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-8 text-xs"
              onClick={packPdf}
              disabled={busy === "pdf-all"}
              data-testid={`pdf-pack-${packCode}`}
            >
              <FileDown className="h-3.5 w-3.5 mr-1" />
              PDF all
            </Button>
          </div>
        ) : null}
      </div>
      <div className="space-y-1.5">
        {items.map((item) => (
          <div
            key={item.id}
            className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border/70 bg-card px-2.5 py-1.5"
          >
            <div className="min-w-0">
              <p className="text-[11px] font-semibold text-foreground">{item.label}</p>
              <p className="font-mono text-[10px] text-muted-foreground truncate" title={item.code}>
                {item.code}
              </p>
            </div>
            <VoucherDocumentActions
              label={item.label}
              voucherId={item.id}
              voucherCode={item.code}
            />
          </div>
        ))}
      </div>
    </div>
  );
}
